import React, { useState, useEffect, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { Picker } from '@react-native-picker/picker';
import { useFocusEffect } from '@react-navigation/native';
import { CustomInput } from '../components/CustomInput';
import { CustomButton } from '../components/CustomButton';
import { DatePickerInput } from '../components/DatePickerInput';
import { AppModal, ModalType } from '../components/AppModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { theme } from '../constants/theme';
import ApiService from '../services/ApiService';
import { DairyRecord, DairyRecordType, Customer, PaymentStatus } from '../models/types';
import { generateId, getISODateString, decimalValidation, parseMilkQuantityToLiters } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';

const AddDairyRecordScreen = ({ navigation, route }: any) => {
    const existingRecord = route.params?.existingRecord as DairyRecord | undefined;
    const isEditMode = !!existingRecord;
    const { user } = useAuth();

    const [loading, setLoading] = useState(false);
    const [recordType, setRecordType] = useState<DairyRecordType>(existingRecord?.recordType || 'sale');
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        isSuccess?: boolean;
    }>({ visible: false, type: 'info', title: '', message: '' });

    // Helper for default record time based on current hour
    const getDefaultRecordTime = (): 'morning' | 'evening' => {
        const hour = new Date().getHours();
        return hour < 12 ? 'morning' : 'evening';
    };

    const [formData, setFormData] = useState({
        date: existingRecord?.date || getISODateString(new Date()),
        recordTime: existingRecord?.recordTime || getDefaultRecordTime(),
        quantity: existingRecord?.quantity?.toString() || '',
        pricePerUnit: existingRecord?.pricePerUnit?.toString() || '',
        totalAmount: existingRecord?.totalAmount?.toString() || '',
        customerName: existingRecord?.customerName || '',
        supplierName: existingRecord?.supplierName || '',
        customerId: existingRecord?.customerId || '',
        amountPaid: existingRecord?.amountPaid?.toString() || '',
        notes: existingRecord?.notes || '',
    });


    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Load customers based on record type
    const loadCustomers = async () => {
        try {
            const customerType = recordType === 'sale' ? 'sale' : recordType === 'purchase' ? 'purchase' : undefined;
            if (customerType) {
                const data = await ApiService.getCustomers(customerType);
                setCustomers(data);
            } else {
                setCustomers([]);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    useFocusEffect(
        useCallback(() => {
            loadCustomers();
        }, [recordType])
    );

    // When customer is selected, update customerName/supplierName
    useEffect(() => {
        if (formData.customerId) {
            const customer = customers.find(c => c.id === formData.customerId);
            if (customer) {
                if (recordType === 'sale') {
                    setFormData(prev => ({ ...prev, customerName: customer.name }));
                } else if (recordType === 'purchase') {
                    setFormData(prev => ({ ...prev, supplierName: customer.name }));
                }
            }
        }
    }, [formData.customerId, customers, recordType]);


    const handleModalClose = () => {
        const isSuccess = modal.isSuccess;
        setModal(prev => ({ ...prev, visible: false }));
        if (isSuccess) {
            navigation.goBack();
        }
    };

    const validate = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.date.trim()) {
            newErrors.date = 'Date is required';
        }

        if (!formData.quantity) {
            newErrors.quantity = 'Quantity is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const calculateTotal = () => {
        const qty = parseMilkQuantityToLiters(formData.quantity);
        const price = parseFloat(formData.pricePerUnit) || 0;
        const total = qty * price;
        setFormData(prev => ({ ...prev, totalAmount: total > 0 ? total.toString() : '' }));
    };

    // Calculate pending amount automatically
    const getPendingAmount = (): number => {
        const total = parseFloat(formData.totalAmount) || 0;
        const paid = parseFloat(formData.amountPaid) || 0;
        return Math.max(0, total - paid);
    };

    const getPaymentStatus = (): PaymentStatus | undefined => {
        const total = parseFloat(formData.totalAmount) || 0;
        const paid = parseFloat(formData.amountPaid) || 0;

        if (total === 0) return undefined;
        if (paid >= total) return 'paid';
        if (paid > 0) return 'partial';
        return 'pending';
    };

    const handleSave = async () => {
        if (!validate()) {
            return;
        }

        setLoading(true);
        try {
            const totalAmount = formData.totalAmount ? parseFloat(formData.totalAmount) : undefined;
            const amountPaid = formData.amountPaid ? parseFloat(formData.amountPaid) : undefined;
            const pendingAmount = totalAmount && amountPaid !== undefined ? Math.max(0, totalAmount - amountPaid) : undefined;

            const record: DairyRecord = {
                id: existingRecord?.id || generateId('DAIRY'),
                recordType,
                date: formData.date,
                recordTime: formData.recordTime,
                quantity: parseMilkQuantityToLiters(formData.quantity),
                pricePerUnit: formData.pricePerUnit
                    ? parseFloat(formData.pricePerUnit)
                    : undefined,
                totalAmount,
                customerName:
                    recordType === 'sale' && formData.customerName
                        ? formData.customerName
                        : undefined,
                supplierName:
                    recordType === 'purchase' && formData.supplierName
                        ? formData.supplierName
                        : undefined,
                customerId: formData.customerId || undefined,
                amountPaid,
                pendingAmount,
                paymentStatus: getPaymentStatus(),
                notes: formData.notes || undefined,
                createdAt: existingRecord?.createdAt || new Date().toISOString(),
            };

            if (isEditMode && existingRecord) {
                await ApiService.updateDairyRecord(existingRecord.id, record);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Record updated successfully',
                    isSuccess: true,
                });
            } else {
                await ApiService.addDairyRecord(record);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Dairy record added successfully',
                    isSuccess: true,
                });
            }
        } catch (error) {
            console.error('Error saving dairy record:', error);
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: 'Failed to save record. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    const pendingAmount = getPendingAmount();
    const showPaymentFields = parseFloat(formData.totalAmount) > 0;

    return (
        <SafeAreaView style={styles.container}>
            <KeyboardAwareScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.contentContainer}
                showsVerticalScrollIndicator={false}
                bottomOffset={20}
            >
                <View style={styles.form}>
                    <View style={styles.typeSelector}>
                        {['purchase', 'sale'].map(type => (
                            <TouchableOpacity
                                key={type}
                                style={[
                                    styles.typeButton,
                                    recordType === type && styles.typeButton_active,
                                ]}
                                onPress={() => {
                                    setRecordType(type as DairyRecordType);
                                    setFormData(prev => ({ ...prev, customerId: '', customerName: '', supplierName: '' }));
                                }}>
                                <Text
                                    style={[
                                        styles.typeButtonText,
                                        recordType === type && styles.typeButtonText_active,
                                    ]}>
                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                </Text>
                            </TouchableOpacity>
                        ))}
                    </View>

                    <DatePickerInput
                        label="Date *"
                        value={formData.date}
                        onChangeDate={date => setFormData({ ...formData, date })}
                        error={errors.date}
                        placeholder="Select date"
                    />

                    {/* Customer Selection for Sale/Purchase */}
                    {(recordType === 'sale' || recordType === 'purchase') && (
                        <View style={styles.pickerContainer}>
                            <Text style={styles.pickerLabel}>
                                {recordType === 'sale' ? 'Select Customer' : 'Select Supplier'}
                            </Text>
                            <View style={styles.pickerWrapper}>
                                <Picker
                                    selectedValue={formData.customerId}
                                    onValueChange={(value) => setFormData({ ...formData, customerId: value })}
                                    style={styles.picker}
                                >
                                    <Picker.Item
                                        label={`-- Select ${recordType === 'sale' ? 'Customer' : 'Supplier'} --`}
                                        value=""
                                    />
                                    {customers.map((customer) => (
                                        <Picker.Item
                                            key={customer.id}
                                            label={customer.name}
                                            value={customer.id}
                                        />
                                    ))}
                                </Picker>
                            </View>
                            <TouchableOpacity
                                style={styles.addCustomerLink}
                                onPress={() => navigation.navigate('AddEditCustomer', {
                                    customerType: recordType === 'sale' ? 'sale' : 'purchase'
                                })}
                            >
                                <Text style={styles.addCustomerText}>
                                    + Add New {recordType === 'sale' ? 'Customer' : 'Supplier'}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    <CustomInput
                        label="Quantity (L.GGG) *"
                        value={formData.quantity}
                        onChangeText={text => {
                            setFormData({ ...formData, quantity: decimalValidation(text) });
                        }}
                        onBlur={calculateTotal}
                        icon="bottle-soda"
                        error={errors.quantity}
                        keyboardType="decimal-pad"
                        placeholder="e.g., 1.100"
                    />
                    <Text style={styles.formatHint}>Format: L.GGG (e.g., 1.100 = 1 L 100 g)</Text>

                    <CustomInput
                        label="Price Per Unit (Optional)"
                        value={formData.pricePerUnit}
                        onChangeText={text => setFormData({ ...formData, pricePerUnit: decimalValidation(text) })}
                        onBlur={calculateTotal}
                        icon="currency-inr"
                        keyboardType="decimal-pad"
                        placeholder="e.g., 55.50"
                    />
                    <Text style={styles.formatHint}>Format: number with decimals (e.g., 55.50)</Text>

                    <CustomInput
                        label="Total Amount (Optional)"
                        value={formData.totalAmount}
                        onChangeText={text => setFormData({ ...formData, totalAmount: text })}
                        icon="cash"
                        keyboardType="decimal-pad"
                        placeholder="Auto-calculated or enter manually"
                    />

                    {/* Payment Fields */}
                    {showPaymentFields && (
                        <View style={styles.paymentSection}>
                            <Text style={styles.sectionTitle}>Payment Details</Text>

                            <CustomInput
                                label="Amount Paid"
                                value={formData.amountPaid}
                                onChangeText={text => setFormData({ ...formData, amountPaid: text })}
                                icon="cash-check"
                                keyboardType="decimal-pad"
                                placeholder="Amount paid now"
                            />

                            <View style={styles.pendingRow}>
                                <Text style={styles.pendingLabel}>Pending Amount:</Text>
                                <Text style={[
                                    styles.pendingValue,
                                    pendingAmount > 0 && styles.pendingValueHighlight
                                ]}>
                                    ₹{pendingAmount.toFixed(2)}
                                </Text>
                            </View>

                            {getPaymentStatus() && (
                                <View style={styles.statusRow}>
                                    <Text style={styles.statusLabel}>Payment Status:</Text>
                                    <View style={[
                                        styles.statusBadge,
                                        getPaymentStatus() === 'paid' && styles.paidBadge,
                                        getPaymentStatus() === 'partial' && styles.partialBadge,
                                        getPaymentStatus() === 'pending' && styles.pendingBadge,
                                    ]}>
                                        <Text style={[
                                            styles.statusText,
                                            getPaymentStatus() === 'paid' && styles.paidText,
                                            getPaymentStatus() === 'partial' && styles.partialText,
                                            getPaymentStatus() === 'pending' && styles.pendingText,
                                        ]}>
                                            {getPaymentStatus()?.toUpperCase()}
                                        </Text>
                                    </View>
                                </View>
                            )}
                        </View>
                    )}

                    {recordType === 'sale' && !formData.customerId && (
                        <CustomInput
                            label="Customer Name (Optional)"
                            value={formData.customerName}
                            onChangeText={text => setFormData({ ...formData, customerName: text })}
                            icon="account"
                            placeholder="Or type name manually"
                        />
                    )}

                    {recordType === 'purchase' && !formData.customerId && (
                        <CustomInput
                            label="Supplier Name (Optional)"
                            value={formData.supplierName}
                            onChangeText={text => setFormData({ ...formData, supplierName: text })}
                            icon="account-tie"
                            placeholder="Or type name manually"
                        />
                    )}

                    <CustomInput
                        label="Notes (Optional)"
                        value={formData.notes}
                        onChangeText={text => setFormData({ ...formData, notes: text })}
                        icon="note-text"
                        placeholder="Any additional information..."
                        multiline
                        numberOfLines={4}
                    />

                    {/* Action Buttons */}
                    <View style={styles.buttonRow}>
                        <TouchableOpacity
                            style={styles.cancelBtn}
                            onPress={() => navigation.goBack()}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.saveBtn}
                            onPress={handleSave}
                            activeOpacity={0.7}
                            disabled={loading}
                        >
                            <Text style={styles.saveBtnText}>{loading ? 'Saving...' : 'Save Record'}</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </KeyboardAwareScrollView>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={handleModalClose}
            />

            <LoadingOverlay visible={loading} message="Saving..." />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    scrollView: {
        flex: 1,
    },
    contentContainer: {
        flexGrow: 1,
    },
    form: {
        padding: theme.spacing.lg,
    },
    typeSelector: {
        flexDirection: 'row',
        marginBottom: theme.spacing.lg,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.xs,
    },
    typeButton: {
        flex: 1,
        paddingVertical: theme.spacing.sm,
        alignItems: 'center',
        borderRadius: theme.borderRadius.sm,
    },
    typeButton_active: {
        backgroundColor: theme.colors.primary,
    },
    typeButtonText: {
        ...theme.typography.body2,
        color: theme.colors.text,
        fontWeight: '600',
    },
    typeButtonText_active: {
        color: theme.colors.surface,
    },
    pickerContainer: {
        marginBottom: theme.spacing.md,
    },
    pickerLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.xs,
    },
    pickerWrapper: {
        borderWidth: 1,
        borderColor: theme.colors.border,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.surface,
    },
    picker: {
        height: 50,
    },
    addCustomerLink: {
        marginTop: theme.spacing.xs,
        alignSelf: 'flex-start',
    },
    addCustomerText: {
        ...theme.typography.caption,
        color: theme.colors.primary,
        fontWeight: '600',
    },
    formatHint: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: -theme.spacing.xs,
        marginBottom: theme.spacing.md,
    },
    paymentSection: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    sectionTitle: {
        ...theme.typography.h6,
        color: theme.colors.text,
        marginBottom: theme.spacing.sm,
    },
    pendingRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: theme.spacing.sm,
        borderTopWidth: 1,
        borderTopColor: theme.colors.divider,
    },
    pendingLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
    },
    pendingValue: {
        ...theme.typography.h6,
        color: theme.colors.text,
    },
    pendingValueHighlight: {
        color: theme.colors.error,
    },
    statusRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: theme.spacing.sm,
    },
    statusLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
    },
    statusBadge: {
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: 4,
        borderRadius: theme.borderRadius.sm,
    },
    paidBadge: {
        backgroundColor: '#E8F5E9',
    },
    partialBadge: {
        backgroundColor: '#FFF3E0',
    },
    pendingBadge: {
        backgroundColor: '#FFEBEE',
    },
    statusText: {
        ...theme.typography.caption,
        fontWeight: '700',
    },
    paidText: {
        color: theme.colors.success,
    },
    partialText: {
        color: theme.colors.warning,
    },
    pendingText: {
        color: theme.colors.error,
    },
    buttonRow: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginTop: theme.spacing.md,
    },
    cancelBtn: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    cancelBtnText: {
        ...theme.typography.button,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
    saveBtn: {
        flex: 2,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.primary,
        ...theme.shadows.sm,
    },
    saveBtnText: {
        ...theme.typography.button,
        color: '#FFFFFF',
        fontWeight: '700',
    },
});

export default AddDairyRecordScreen;
